import React from "react";
import cn from "classnames";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { Button } from "@src/components/atoms/buttons/button/button";
import { OperationProtocolDetails } from "@src/components/atoms/operation-protocol-details/operation-protocol-details";
import { getGatewayOperationProtocolEntries } from "@src/types/gateway-onboarding";
import warningTriangleRedIcon from "@src/assets/svg/warning-triangle-red.svg";
import warningTriangleOrangeIcon from "@src/assets/svg/warning-triangle.svg";
import infoIcon from "@src/assets/svg/info-icon-yellow.svg";
import greenIndicatorIcon from "@src/components/atoms/indicator-text/icons/green-indicator-icon.svg";
import styles from "./add-new-machine-drawer-modal.module.scss";
import { useAddNewMachineDrawerModalContext } from "./add-new-machine-drawer-modal-context";
import { useLanguage } from "@src/language/language-context";
import { getOpcUaErrorDescriptions } from "@src/cloud-connect/utils/opcua-status-codes";

export const AddNewMachineSummaryStep = () => {
  const {
    texts,
    machineName,
    selectedGateway,
    onboardingMachine,
    machineOnboardingSummary,
    showProtocolDetails,
    isPollingSkillOperation,
    canRetrySkillOperation,
    canRetrySkillSetup,
    canUpdateFailedSkills,
    handleRetrySkillOperation,
    handleShowProtocol,
    handleUpdatePortsFromSummary,
    handleFinishSummaryStep,
    handleGoToNewMachine,
  } = useAddNewMachineDrawerModalContext();
  const language = useLanguage();

  const isWarningStatus = machineOnboardingSummary.status === "WARNING";
  const hasFailedSkills = machineOnboardingSummary.failedSkills.length > 0;
  const firstFailedSkill = machineOnboardingSummary.failedSkills[0];
  const hasOpcUaFailure = machineOnboardingSummary.failedSkills.some(
    (failedSkill) => failedSkill.type === "OPCUA",
  );
  const hasPendingOpcUaSkill = machineOnboardingSummary.failedSkills.some(
    (failedSkill) => failedSkill.type === "OPCUA" && failedSkill.isWarning,
  );
  const hasOpcUaIdleFailure = machineOnboardingSummary.failedSkills.some(
    (failedSkill) =>
      failedSkill.type === "OPCUA" &&
      failedSkill.details?.error_code === "OPCUA_CONNECTION_SETUP_IDLE",
  );
  const showCertificateFailureInfo =
    hasOpcUaFailure && !isWarningStatus && !hasOpcUaIdleFailure;
  const failureMessage = showCertificateFailureInfo
    ? texts.certificateGenerationFailureMessage
    : firstFailedSkill?.error;

  // The operation protocol of the first failing skill is the only debuggable
  // trace of an onboarding failure, so it must stay reachable from the summary.
  const protocolEntries = getGatewayOperationProtocolEntries(
    firstFailedSkill?.details,
  );
  const additionalErrorCodes = machineOnboardingSummary.failedSkills.flatMap(
    (failedSkill) => failedSkill.details?.additional_error_codes || [],
  );
  const opcUaErrorDescriptions = getOpcUaErrorDescriptions(
    additionalErrorCodes,
    language,
  );
  const hasProtocolDetails =
    protocolEntries.length > 0 || opcUaErrorDescriptions.length > 0;
  const showProtocolToggle =
    hasFailedSkills && hasProtocolDetails && !isPollingSkillOperation;

  return (
    <>
      <div className={styles["summary-card"]}>
        <div className={styles["summary-gateway-header"]}>
          <img
            src={greenIndicatorIcon}
            alt=""
            className={styles["summary-gateway-icon"]}
          />
          <span className={styles["summary-gateway-text"]}>
            {selectedGateway?.name
              ? `Gateway Name ${selectedGateway.name}`
              : "Gateway Name -"}
          </span>
        </div>

        <div className={styles["summary-body"]}>
          <p className={styles["summary-field-label"]}>
            {texts.machineNameLabel}
          </p>
          <p className={styles["summary-field-value"]}>{machineName || "-"}</p>

          {onboardingMachine?.source_id && (
            <>
              <p className={styles["summary-field-label"]}>UUID</p>
              <p className={styles["summary-field-value"]}>
                {onboardingMachine.source_id}
              </p>
            </>
          )}

          <p className={styles["summary-field-label"]}>
            {texts.summaryInstalledSkillsLabel}
          </p>

          {(machineOnboardingSummary.status === "SKIPPED" ||
            (machineOnboardingSummary.installedSkills.length === 0 &&
              machineOnboardingSummary.failedSkills.length === 0)) && (
            <p className={styles["summary-field-value-muted"]}>-</p>
          )}

          {machineOnboardingSummary.installedSkills.map((skill) => (
            <div
              key={`installed-${skill}`}
              className={styles["summary-skill-row"]}
            >
              <span className={styles["summary-skill-mark-success"]}>✓</span>
              <span>{skill}</span>
            </div>
          ))}

          {machineOnboardingSummary.failedSkills.map((failedSkill) => {
            const isSkillWarning = isWarningStatus || failedSkill.isWarning;

            return (
              <div
                key={`failed-${failedSkill.type}`}
                className={cn(styles["summary-skill-row-error"], {
                  [styles["summary-skill-row-warning"]]: isSkillWarning,
                })}
              >
                <span
                  className={cn(styles["summary-skill-mark-error"], {
                    [styles["summary-skill-mark-warning"]]: isSkillWarning,
                  })}
                >
                  ✕
                </span>
                <span>{failedSkill.type}</span>
              </div>
            );
          })}

          {hasFailedSkills &&
            !showCertificateFailureInfo &&
            !hasPendingOpcUaSkill && (
              <div className={styles["summary-failure-row"]}>
                <div
                  className={cn(styles["summary-failure-message"], {
                    [styles["summary-failure-message--warning"]]:
                      isWarningStatus,
                  })}
                >
                  <img
                    src={
                      isWarningStatus
                        ? warningTriangleOrangeIcon
                        : warningTriangleRedIcon
                    }
                    alt=""
                  />
                  <span>{failureMessage}</span>
                </div>
              </div>
            )}

          {hasFailedSkills && showCertificateFailureInfo && (
            <div className={styles["summary-certificate-info"]}>
              <img src={infoIcon} alt="" />
              <p>{failureMessage}</p>
            </div>
          )}

          {showProtocolToggle && (
            <Button
              fun={handleShowProtocol}
              text={
                showProtocolDetails ? texts.hideProtocol : texts.showProtocol
              }
              variant={["no-outline-with-green20-hover", "color-blue"]}
            />
          )}

          {showProtocolToggle &&
            showProtocolDetails &&
            !isPollingSkillOperation && (
              <OperationProtocolDetails
                className={styles["summary-protocol-details"]}
                protocolEntries={protocolEntries}
                opcUaErrorDescriptions={opcUaErrorDescriptions}
                opcUaErrorDetailsTitle={texts.opcUaErrorDetailsTitle}
              />
            )}
        </div>
      </div>

      {canRetrySkillOperation && (
        <ButtonWithLoader
          fun={handleRetrySkillOperation}
          text={texts.summaryRetryButton}
          loading={isPollingSkillOperation}
          disabled={isPollingSkillOperation}
          variant={["full-width"]}
        />
      )}

      {hasFailedSkills && canRetrySkillSetup && !isWarningStatus && (
        <ButtonWithLoader
          fun={handleUpdatePortsFromSummary}
          text={texts.summaryRetryButton}
          variant={["full-width"]}
        />
      )}

      {hasFailedSkills && canUpdateFailedSkills && !isWarningStatus && (
        <ButtonWithLoader
          fun={handleUpdatePortsFromSummary}
          text={texts.summaryUpdateAndRetryButton}
          variant={["full-width"]}
        />
      )}

      <ButtonWithLoader
        variant={["full-width"]}
        fun={handleFinishSummaryStep}
        text={texts.summaryFinishButton}
        loading={false}
      />

      <ButtonWithLoader
        variant={["full-width"]}
        fun={handleGoToNewMachine}
        text={texts.summaryGoToNewMachineButton}
        loading={false}
      />
    </>
  );
};
